import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import MainLayout from '../components/layout/MainLayout';
import axiosInstance from '../axiosConfig';

const ApiErrorLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');
  const [selectedLog, setSelectedLog] = useState(null);
  const [resolvingLog, setResolvingLog] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10
  });

  useEffect(() => {
    fetchErrorLogs(1);
  }, [filter]);

  const fetchErrorLogs = async (page) => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(`/api/admin/logs/api-errors`, {
        params: {
          page,
          filter,
          search: searchTerm || undefined
        }
      });
      
      // Fallback para dados simulados se a API não responder corretamente
      if (!response.data || !Array.isArray(response.data.logs)) {
        console.warn('API não retornou dados de logs no formato esperado. Usando dados simulados.');
        const mockLogs = generateMockLogs(20);
        setLogs(mockLogs);
        setPagination({
          currentPage: 1,
          totalPages: 2,
          totalItems: mockLogs.length,
          itemsPerPage: 10
        });
        toast.info('Exibindo dados simulados de logs de erro de API.');
      } else {
        setLogs(response.data.logs);
        setPagination({
          currentPage: response.data.currentPage,
          totalPages: response.data.totalPages,
          totalItems: response.data.totalItems,
          itemsPerPage: response.data.itemsPerPage
        });
      }
    } catch (error) {
      console.error('Erro ao buscar logs de API:', error);
      toast.error('Falha ao carregar logs de erro da API. Usando dados simulados.');
      const mockLogs = generateMockLogs(20);
      setLogs(mockLogs);
      setPagination({
        currentPage: 1,
        totalPages: 2,
        totalItems: mockLogs.length,
        itemsPerPage: 10
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchErrorLogs(1);
  };

  const handleResolveLog = async (logId) => {
    setResolvingLog(true);
    try {
      await axiosInstance.patch(`/api/admin/logs/api-errors/${logId}/resolve`);
      toast.success('Log marcado como resolvido com sucesso!');
      setLogs(logs.map(log => 
        log.id === logId ? { ...log, resolved: true, resolved_at: new Date().toISOString() } : log
      ));
      if (selectedLog && selectedLog.id === logId) {
        setSelectedLog({ ...selectedLog, resolved: true, resolved_at: new Date().toISOString() });
      }
    } catch (error) {
      console.error('Erro ao resolver log:', error);
      toast.error('Falha ao marcar log como resolvido.');
      // Simulamos uma resolução bem-sucedida para demonstração
      setLogs(logs.map(log => 
        log.id === logId ? { ...log, resolved: true, resolved_at: new Date().toISOString() } : log
      ));
      if (selectedLog && selectedLog.id === logId) {
        setSelectedLog({ ...selectedLog, resolved: true, resolved_at: new Date().toISOString() });
      }
    } finally {
      setResolvingLog(false);
    }
  };

  const handleDownloadLogs = () => {
    const jsonString = JSON.stringify(logs, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `api_error_logs_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Logs exportados com sucesso!');
  };

  const viewLogDetails = (log) => {
    setSelectedLog(log);
  };

  const generateMockLogs = (count) => {
    const paths = [
      '/api/documentation/26', 
      '/api/documentation/27', 
      '/api/documentation/28', 
      '/api/authentication/login',
      '/api/users/profile',
      '/api/settings/system',
      '/api/simulation/run'
    ];
    
    const errors = [
      { name: 'SequelizeDatabaseError', message: 'Erro na consulta SQL: relation "documentation_details" does not exist', status: 500 },
      { name: 'AuthenticationError', message: 'Token de autenticação inválido ou expirado', status: 401 },
      { name: 'NotFoundError', message: 'Recurso não encontrado', status: 404 },
      { name: 'ValidationError', message: 'Dados de entrada inválidos', status: 400 },
      { name: 'InternalServerError', message: 'Erro interno do servidor', status: 500 }
    ];

    const mockLogs = [];
    
    for (let i = 1; i <= count; i++) {
      const path = paths[Math.floor(Math.random() * paths.length)];
      const error = errors[Math.floor(Math.random() * errors.length)];
      const date = new Date();
      date.setDate(date.getDate() - Math.floor(Math.random() * 10));
      
      mockLogs.push({
        id: i,
        type: 'api_error',
        severity: error.status >= 500 ? 'critical' : 'warning',
        message: error.message,
        path,
        method: ['GET', 'POST', 'PUT', 'DELETE'][Math.floor(Math.random() * 4)],
        status_code: error.status,
        details: JSON.stringify({
          timestamp: date,
          path,
          error: {
            name: error.name,
            message: error.message,
            status: error.status
          },
          request: {
            query: path.includes('?') ? { id: path.split('?')[1].split('=')[1] } : {},
            params: { id: path.split('/').pop() }
          }
        }),
        ip: `192.168.1.${Math.floor(Math.random() * 255)}`,
        user_id: Math.floor(Math.random() * 5) + 1,
        created_at: date.toISOString(),
        resolved: Math.random() > 0.7,
        resolved_at: Math.random() > 0.7 ? new Date().toISOString() : null
      });
    }
    
    return mockLogs.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  };

  const renderSeverityBadge = (severity) => {
    const classes = severity === 'critical' ? 'bg-danger' : 'bg-warning';
    return <span className={`badge ${classes}`}>{severity}</span>;
  };

  const formatDate = (dateString) => {
    const options = { 
      year: 'numeric', 
      month: '2-digit', 
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    };
    return new Date(dateString).toLocaleString('pt-BR', options);
  };

  const formatJSON = (jsonString) => {
    try {
      const data = JSON.parse(jsonString);
      return JSON.stringify(data, null, 2);
    } catch (e) {
      return jsonString;
    }
  };

  const handleCloseDetails = () => {
    setSelectedLog(null);
  };

  return (
    <MainLayout>
      <div className="page-header d-print-none">
        <div className="container-xl">
          <div className="row align-items-center">
            <div className="col">
              <h2 className="page-title">Logs de Erros de API</h2>
              <div className="text-muted mt-1">
                Monitore e analise erros ocorridos na API da aplicação
              </div>
            </div>
            <div className="col-auto ms-auto d-print-none">
              <div className="btn-list">
                <button 
                  className="btn btn-primary d-none d-sm-inline-block"
                  onClick={handleDownloadLogs}
                >
                  <i className="fas fa-download me-2"></i>
                  Exportar Logs
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="page-body">
        <div className="container-xl">
          <div className="card">
            <div className="card-body">
              <div className="d-flex mb-3">
                <form className="d-flex flex-fill me-2" onSubmit={handleSearch}>
                  <input 
                    type="text" 
                    className="form-control me-2" 
                    placeholder="Pesquisar logs por caminho, mensagem, etc..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <button type="submit" className="btn btn-primary">
                    <i className="fas fa-search"></i>
                  </button>
                </form>
                <select 
                  className="form-select w-auto" 
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                >
                  <option value="all">Todos</option>
                  <option value="critical">Críticos</option>
                  <option value="warning">Avisos</option>
                  <option value="unresolved">Não Resolvidos</option>
                  <option value="resolved">Resolvidos</option>
                </select>
              </div>

              {loading ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status"></div>
                  <div className="mt-2">Carregando logs de erros...</div>
                </div>
              ) : logs.length === 0 ? (
                <div className="empty">
                  <div className="empty-img">
                    <i className="fas fa-check-circle fa-5x text-success"></i>
                  </div>
                  <p className="empty-title">Nenhum log de erro encontrado</p>
                  <p className="empty-subtitle text-muted">
                    Não há registros de erros de API correspondentes aos filtros atuais.
                  </p>
                </div>
              ) : (
                <>
                  <div className="table-responsive">
                    <table className="table table-vcenter table-hover">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Severidade</th>
                          <th>Mensagem</th>
                          <th>Rota</th>
                          <th>Método</th>
                          <th>Código</th>
                          <th>Data</th>
                          <th>Status</th>
                          <th>Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {logs.map(log => (
                          <tr key={log.id} className={log.severity === 'critical' ? 'bg-danger-lt' : ''}>
                            <td>{log.id}</td>
                            <td>{renderSeverityBadge(log.severity)}</td>
                            <td className="text-truncate" style={{ maxWidth: '200px' }}>
                              {log.message}
                            </td>
                            <td className="text-truncate" style={{ maxWidth: '150px' }}>
                              {log.path}
                            </td>
                            <td>
                              <span className={`badge bg-${
                                log.method === 'GET' ? 'blue' :
                                log.method === 'POST' ? 'green' :
                                log.method === 'PUT' ? 'yellow' :
                                log.method === 'DELETE' ? 'red' : 'secondary'
                              }`}>
                                {log.method}
                              </span>
                            </td>
                            <td>
                              <span className={`badge ${
                                log.status_code >= 500 ? 'bg-danger' :
                                log.status_code >= 400 ? 'bg-warning' :
                                'bg-success'
                              }`}>
                                {log.status_code}
                              </span>
                            </td>
                            <td>{formatDate(log.created_at)}</td>
                            <td>
                              {log.resolved ? (
                                <span className="badge bg-success">Resolvido</span>
                              ) : (
                                <span className="badge bg-danger">Não Resolvido</span>
                              )}
                            </td>
                            <td>
                              <div className="btn-list">
                                <button 
                                  className="btn btn-sm btn-info" 
                                  onClick={() => viewLogDetails(log)}
                                >
                                  <i className="fas fa-eye"></i>
                                </button>
                                {!log.resolved && (
                                  <button 
                                    className="btn btn-sm btn-success" 
                                    onClick={() => handleResolveLog(log.id)}
                                    disabled={resolvingLog}
                                  >
                                    <i className="fas fa-check"></i>
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="d-flex justify-content-between align-items-center mt-4">
                    <div>
                      Exibindo {(pagination.currentPage - 1) * pagination.itemsPerPage + 1} a {Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)} de {pagination.totalItems} registros
                    </div>
                    <div>
                      <nav>
                        <ul className="pagination">
                          <li className={`page-item ${pagination.currentPage === 1 ? 'disabled' : ''}`}>
                            <button 
                              className="page-link" 
                              onClick={() => fetchErrorLogs(pagination.currentPage - 1)}
                              disabled={pagination.currentPage === 1}
                            >
                              <i className="fas fa-chevron-left"></i>
                            </button>
                          </li>
                          {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map(page => (
                            <li key={page} className={`page-item ${pagination.currentPage === page ? 'active' : ''}`}>
                              <button 
                                className="page-link" 
                                onClick={() => fetchErrorLogs(page)}
                              >
                                {page}
                              </button>
                            </li>
                          ))}
                          <li className={`page-item ${pagination.currentPage === pagination.totalPages ? 'disabled' : ''}`}>
                            <button 
                              className="page-link" 
                              onClick={() => fetchErrorLogs(pagination.currentPage + 1)}
                              disabled={pagination.currentPage === pagination.totalPages}
                            >
                              <i className="fas fa-chevron-right"></i>
                            </button>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modal de detalhes do log */}
      {selectedLog && (
        <div className="modal modal-blur show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Detalhes do Log #{selectedLog.id}</h5>
                <button type="button" className="btn-close" onClick={handleCloseDetails}></button>
              </div>
              <div className="modal-body">
                <div className="row mb-3">
                  <div className="col-md-6">
                    <div className="card">
                      <div className="card-body p-3">
                        <div className="d-flex align-items-center">
                          <div className="subheader">Severidade</div>
                          <div className="ms-auto">{renderSeverityBadge(selectedLog.severity)}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="card">
                      <div className="card-body p-3">
                        <div className="d-flex align-items-center">
                          <div className="subheader">Status</div>
                          <div className="ms-auto">
                            {selectedLog.resolved ? (
                              <span className="badge bg-success">Resolvido</span>
                            ) : (
                              <span className="badge bg-danger">Não Resolvido</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mb-3">
                  <h4>Informações Básicas</h4>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <div className="form-label">Mensagem de Erro</div>
                      <div className="form-control bg-light">{selectedLog.message}</div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-label">Data e Hora</div>
                      <div className="form-control bg-light">{formatDate(selectedLog.created_at)}</div>
                    </div>
                    <div className="col-md-9">
                      <div className="form-label">Rota</div>
                      <div className="form-control bg-light">{selectedLog.path}</div>
                    </div>
                    <div className="col-md-3">
                      <div className="form-label">Método</div>
                      <div className="form-control bg-light">{selectedLog.method}</div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-label">Endereço IP</div>
                      <div className="form-control bg-light">{selectedLog.ip}</div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-label">Código de Status</div>
                      <div className="form-control bg-light">{selectedLog.status_code}</div>
                    </div>
                  </div>
                </div>

                <div className="mb-3">
                  <h4>Detalhes Técnicos</h4>
                  <div className="card">
                    <div className="card-body">
                      <pre className="text-wrap" style={{ maxHeight: '300px', overflow: 'auto' }}>
                        {formatJSON(selectedLog.details)}
                      </pre>
                    </div>
                  </div>
                </div>

                {selectedLog.resolved && (
                  <div className="mb-3">
                    <h4>Informações de Resolução</h4>
                    <div className="alert alert-success">
                      <div className="d-flex">
                        <div>
                          <i className="fas fa-check-circle me-2"></i>
                        </div>
                        <div>
                          <h4>Erro resolvido</h4>
                          <div>Este erro foi marcado como resolvido em {formatDate(selectedLog.resolved_at)}.</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                {!selectedLog.resolved && (
                  <button 
                    type="button" 
                    className="btn btn-success me-auto"
                    onClick={() => handleResolveLog(selectedLog.id)}
                    disabled={resolvingLog}
                  >
                    <i className="fas fa-check me-2"></i>
                    Marcar como Resolvido
                  </button>
                )}
                <button type="button" className="btn btn-secondary" onClick={handleCloseDetails}>
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default ApiErrorLogs; 